/**
 * API editor — a dedicated page (not a popup) to build, test and map one API.
 *
 *   left  : 01 Details · 02 Build your request (Request / Authentication / Headers / Advanced)
 *           · 03 Map the response (fields are PICKED from the real response)
 *   right : sticky Test & response — sample variables, real call, JSON reader
 *
 * The saved API carries its whole request + response mapping; the "Call API"
 * flow node just picks it. Mapped values land in a flow variable ({{name}},
 * automation-only, lives for the run) or in a contact/lead field (durable —
 * chosen from the fields already defined under Settings › Custom Fields).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Menu,
  MenuItem,
  ListSubheader,
  Stack,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Link,
  Select,
  InputBase,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  DeleteOutline,
  FlashOn,
  Terminal,
  ContentCopy,
  DataObject,
  Webhook,
  Code,
  VpnKey,
  Tune,
  ArrowForward,
  Check,
  Refresh,
  OpenInNew,
  ExpandMore,
  ChevronRight,
  Lock,
} from '@mui/icons-material';
import { usePlanFeature } from '@/hooks/usePlanFeature';
import adminApi, { ExternalApiClient, FieldDefinition } from '@/services/admin.api';
import FlowVariablePicker from './FlowVariablePicker';
import apiConnectionApi, {
  ApiConnectionInput,
  ApiRequestDef,
  BodyMode,
  HttpMethod,
  RequiresAuthLevel,
  ResponseMapping,
  ServiceAuthType,
  TestPayload,
  TestResult,
  VariableTarget,
} from '@/services/apiConnection.api';
import {
  KeyValue,
  ResponseField,
  insertAtCaret,
  flattenJsonLeaves,
  previewValue,
  renderPreviewUrl,
  childPath,
  extractVariables,
  isValidVariableName,
  joinEndpoint,
  resolveJsonPath,
  splitEndpoint,
  suggestVariableName,
} from './apiConnectionUtils';

type Kind = 'public' | 'external_client';
type PublicAuth = Exclude<ServiceAuthType, 'external_client'>;

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const AUTH_LABELS: Record<PublicAuth, string> = {
  none: 'None',
  bearer: 'Bearer token',
  basic: 'Basic (user:pass)',
  api_key_header: 'API key header',
  hmac: 'HMAC request signing',
  oauth2_client_credentials: 'OAuth2 (client credentials)',
};
const AUTH_LEVEL_LABELS: Record<RequiresAuthLevel, string> = {
  none: 'Any — store-wide data',
  identified: 'Specific — identified customer',
  verified: 'Verified — stepped-up customer',
};
/** One-word name for inline sentences ("required level: Specific"). */
const AUTH_LEVEL_SHORT: Record<RequiresAuthLevel, string> = {
  none: 'Any',
  identified: 'Specific',
  verified: 'Verified',
};
const AUTH_RANK: Record<RequiresAuthLevel, number> = { none: 0, identified: 1, verified: 2 };
const MONO = { fontFamily: 'monospace', fontSize: 12.5 } as const;

interface Form {
  name: string;
  description: string;
  kind: Kind;
  externalClientId: string;
  status: 'active' | 'disabled';
  method: HttpMethod;
  endpoint: string;
  headers: KeyValue[];
  bodyMode: BodyMode;
  body: string;
  authType: PublicAuth;
  credential: string;
  apiKeyHeaderName: string;
  hmacSecret: string;
  oauthTokenUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthScopes: string;
  requiresAuthLevel: RequiresAuthLevel;
  sendUserContext: boolean;
  userContextSecret: string;
  timeoutMs: string;
  mappings: ResponseMapping[];
}

const emptyForm: Form = {
  name: '',
  description: '',
  kind: 'public',
  externalClientId: '',
  status: 'active',
  method: 'GET',
  endpoint: '',
  headers: [],
  bodyMode: 'none',
  body: '',
  authType: 'none',
  credential: '',
  apiKeyHeaderName: '',
  hmacSecret: '',
  oauthTokenUrl: '',
  oauthClientId: '',
  oauthClientSecret: '',
  oauthScopes: '',
  requiresAuthLevel: 'none',
  sendUserContext: false,
  userContextSecret: '',
  timeoutMs: '',
  mappings: [],
};

/**
 * Pull a human-readable message out of an axios error. The API envelopes errors
 * as `{ success:false, error:{ code, message, details } }`, so the message lives
 * at `response.data.error.message` — reading `data.message` (the old path) always
 * missed it and fell back to axios's "Request failed with status code 400". When
 * the server sent Zod field `details`, append the first couple so the user knows
 * which field to fix.
 */
function apiErrorMsg(e: any, fallback: string): string {
  const body = e?.response?.data;
  const err = body?.error;
  let msg: string | undefined =
    (typeof err === 'string' ? err : err?.message) ?? body?.message ?? e?.message;
  const details = err?.details;
  if (Array.isArray(details) && details.length) {
    const parts = details
      .slice(0, 2)
      .map((d: any) => (d?.field ? `${d.field}: ${d.message}` : d?.message))
      .filter(Boolean);
    if (parts.length) msg = `${msg ? `${msg} — ` : ''}${parts.join('; ')}`;
  }
  return msg || fallback;
}

/** Turn the form into the request definition + the connection fields. */
function buildInput(f: Form): { input?: ApiConnectionInput & { baseUrl: string }; request?: ApiRequestDef; error?: string } {
  if (!f.name.trim()) return { error: 'Give the API a name.' };
  const split = splitEndpoint(f.endpoint);
  if (!split) {
    return { error: 'Enter a full https:// URL. The host (e.g. api.example.com) can’t contain variables.' };
  }
  for (const m of f.mappings) {
    if (!m.source.trim()) return { error: 'Every mapping needs a response path.' };
    if (m.target.kind === 'flow' && !isValidVariableName(m.target.name)) {
      return { error: `“${m.target.name || '(empty)'}” isn’t a valid variable name — use letters, numbers and _ , starting with a letter.` };
    }
    if (m.target.kind !== 'flow' && !m.target.key) return { error: 'Pick a field for each contact/lead mapping.' };
  }
  const names = f.mappings.filter((m) => m.target.kind === 'flow').map((m) => (m.target as { name: string }).name);
  if (new Set(names).size !== names.length) return { error: 'Two mappings use the same variable name.' };

  // An empty JSON/raw body means "no body" (e.g. a POST whose id is in the path),
  // not an invalid template — otherwise the server would JSON.parse('') and block.
  const bodyText = (f.body ?? '').trim();
  let bodyMode: BodyMode = f.method === 'GET' ? 'none' : f.bodyMode;
  if ((bodyMode === 'json' || bodyMode === 'raw') && !bodyText) bodyMode = 'none';
  // Catch a genuinely malformed JSON body here, with a clear message, rather than
  // letting it fail only when the test request is sent. Tokens live inside strings,
  // so a valid template still parses.
  if (bodyMode === 'json') {
    try {
      JSON.parse(f.body);
    } catch {
      return { error: 'The JSON body isn’t valid JSON. Put {{variables}} inside quotes, or switch Body to “None”.' };
    }
  }

  const request: ApiRequestDef = { method: f.method, bodyMode };
  if (split.path) request.path = split.path;
  if (split.queryParams.length) request.queryParams = split.queryParams;
  const headers = f.headers.filter((h) => h.key.trim());
  if (headers.length) request.headers = headers;
  if (f.method !== 'GET' && bodyMode !== 'none') request.body = f.body;
  if (f.mappings.length) request.responseMappings = f.mappings;

  const auth: NonNullable<ApiConnectionInput['auth']> =
    f.kind === 'external_client'
      ? { type: 'external_client', externalClientId: f.externalClientId }
      : { type: f.authType };
  if (f.kind === 'public') {
    if (f.credential) auth.credential = f.credential;
    if (f.apiKeyHeaderName) auth.apiKeyHeaderName = f.apiKeyHeaderName;
    if (f.hmacSecret) auth.hmacSecret = f.hmacSecret;
    if (f.sendUserContext && f.userContextSecret) auth.userContextSecret = f.userContextSecret;
    if (f.authType === 'oauth2_client_credentials') {
      const o: NonNullable<typeof auth.oauth2> = {};
      if (f.oauthTokenUrl) o.tokenUrl = f.oauthTokenUrl;
      if (f.oauthScopes) o.scopes = f.oauthScopes.split(/\s+/).filter(Boolean);
      if (f.oauthClientId) o.clientId = f.oauthClientId;
      if (f.oauthClientSecret) o.clientSecret = f.oauthClientSecret;
      auth.oauth2 = o;
    }
  } else if (f.kind === 'external_client' && !f.externalClientId) {
    return { error: 'Choose which external client this API belongs to.' };
  }

  const input: ApiConnectionInput & { baseUrl: string } = {
    name: f.name.trim(),
    baseUrl: split.baseUrl,
    auth,
    request,
    requiresAuthLevel: f.requiresAuthLevel,
    sendUserContext: f.sendUserContext,
    status: f.status,
  };
  if (f.description.trim()) input.description = f.description.trim();
  if (f.timeoutMs) input.timeoutMs = Number(f.timeoutMs);
  return { input, request };
}

// ─── Design tokens — neutral surfaces; green is reserved for the two primary actions ──
const ACCENT = '#1d6b4a';
const ACCENT_HOVER = '#165a3d';
const CARD = { border: 1, borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper' } as const;
const FIELD_SX = { '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'background.paper' } } as const;

/** One titled section of the insert menu. */
interface InsertVarGroup {
  label: string;
  items: Array<{ token: string; hint?: string }>;
}

/**
 * A `{}` button that drops a `{{token}}` into a field at its caret. `onPick`
 * gets the token; the caller inserts it. Mouse-down is suppressed so the target
 * field keeps focus (and its caret) while the menu opens.
 *
 * Options are grouped (Customer / Contact fields / Flow variables / …) so an
 * author can tell apart a customer identity field, a stored contact attribute,
 * and a flow variable — and every token shown is one the engine actually
 * resolves (the picker is the source of truth for what's available).
 */
const InsertVarMenu: React.FC<{ groups: InsertVarGroup[]; onPick: (token: string) => void; title?: string }> = ({
  groups,
  onPick,
  title = 'Insert a variable',
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const nonEmpty = groups.filter((g) => g.items.length > 0);
  return (
    <>
      <Tooltip title={title}>
        <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={(e) => setAnchor(e.currentTarget)}>
          <DataObject sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        MenuListProps={{ dense: true }}
        slotProps={{ paper: { sx: { maxHeight: 380 } } }}
      >
        {nonEmpty.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 12.5 }}>
            No variables yet
          </MenuItem>
        )}
        {nonEmpty.flatMap((g) => [
          <ListSubheader
            key={`h-${g.label}`}
            disableSticky
            sx={{ fontSize: 11, lineHeight: '26px', fontWeight: 700, color: 'text.secondary', bgcolor: 'transparent', textTransform: 'uppercase', letterSpacing: 0.4 }}
          >
            {g.label}
          </ListSubheader>,
          ...g.items.map((it) => (
            <MenuItem
              key={`${g.label}-${it.token}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onPick(it.token);
                setAnchor(null);
              }}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
            >
              <span style={{ fontFamily: 'monospace', fontSize: 12.5 }}>{`{{${it.token}}}`}</span>
              {it.hint && <span style={{ opacity: 0.6, fontSize: 11.5 }}>{it.hint}</span>}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
};

// ─── Response viewer: a JSON reader where every value is click-to-map ────────

const JsonNode: React.FC<{
  label?: string;
  value: unknown;
  path: string;
  depth: number;
  mapped: Set<string>;
  onPick: (path: string) => void;
}> = ({ label, value, path, depth, mapped, onPick }) => {
  const isObj = value !== null && typeof value === 'object';
  const [open, setOpen] = useState(depth < 2);
  const isMapped = mapped.has(path);

  if (!isObj) {
    const color = typeof value === 'string' ? '#0f766e' : typeof value === 'number' ? '#b45309' : '#7c3aed';
    return (
      <Tooltip title={isMapped ? 'Already mapped' : 'Click to map this value'} placement="left" enterDelay={300}>
        <Box
          onClick={() => !isMapped && onPick(path)}
          sx={{
            ...MONO,
            ml: depth * 1.75,
            my: '1px',
            px: 0.75,
            py: 0.25,
            cursor: isMapped ? 'default' : 'pointer',
            borderRadius: '6px',
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            bgcolor: isMapped ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: isMapped ? 'action.selected' : 'action.hover' },
          }}
        >
          {label !== undefined && <span style={{ opacity: 0.7 }}>{label}:</span>}
          <span style={{ color, wordBreak: 'break-all' }}>{typeof value === 'string' ? `"${value.length > 90 ? `${value.slice(0, 90)}…` : value}"` : String(value)}</span>
          {isMapped && <Check sx={{ fontSize: 14, color: ACCENT, ml: 'auto' }} />}
        </Box>
      </Tooltip>
    );
  }

  const entries: Array<[string | number, unknown]> = Array.isArray(value)
    ? value.slice(0, 20).map((v, i) => [i, v])
    : Object.entries(value as Record<string, unknown>);
  const total = Array.isArray(value) ? value.length : entries.length;
  return (
    <Box>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ ...MONO, ml: depth * 1.75, px: 0.25, py: 0.25, cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: '6px', '&:hover': { bgcolor: 'action.hover' } }}
      >
        {open ? <ExpandMore sx={{ fontSize: 16 }} /> : <ChevronRight sx={{ fontSize: 16 }} />}
        {label !== undefined && <span style={{ opacity: 0.7, marginRight: 6 }}>{label}:</span>}
        <span style={{ opacity: 0.55 }}>{Array.isArray(value) ? `[ ${total} ]` : `{ ${total} }`}</span>
      </Box>
      {open &&
        entries.map(([k, v]) => (
          <JsonNode key={String(k)} label={Array.isArray(value) ? `[${k}]` : String(k)} value={v} path={childPath(path, k)} depth={depth + 1} mapped={mapped} onPick={onPick} />
        ))}
      {open && Array.isArray(value) && total > 20 && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: (depth + 1) * 1.75 + 1 }}>
          … {total - 20} more not shown
        </Typography>
      )}
    </Box>
  );
};

// ─── Small layout pieces ─────────────────────────────────────────────────────

const SectionHeader: React.FC<{ n: string; title: string; right?: React.ReactNode }> = ({ n, title, right }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
    <Box sx={{ ...CARD, borderRadius: '8px', px: 1, py: 0.25, fontSize: 12, fontWeight: 600, color: 'text.secondary', bgcolor: 'action.hover', lineHeight: 1.6 }}>{n}</Box>
    <Typography fontWeight={700} fontSize={17} sx={{ flex: 1 }}>
      {title}
    </Typography>
    {right}
  </Stack>
);

const Label: React.FC<{ children: React.ReactNode; optional?: boolean }> = ({ children, optional }) => (
  <Typography fontSize={13.5} fontWeight={600} mb={0.75}>
    {children}
    {optional && (
      <Typography component="span" fontSize={12} color="text.secondary" fontWeight={400} ml={1}>
        Optional
      </Typography>
    )}
  </Typography>
);

const Tab: React.FC<{ active: boolean; icon: React.ReactNode; label: string; count?: number; onClick: () => void }> = ({ active, icon, label, count, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      all: 'unset',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 0.5,
      py: 1.25,
      mr: 2.5,
      fontSize: 13.5,
      fontWeight: active ? 700 : 500,
      color: active ? 'text.primary' : 'text.secondary',
      borderBottom: 2,
      borderColor: active ? 'text.primary' : 'transparent',
      mb: '-1px',
      '&:hover': { color: 'text.primary' },
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', borderRadius: '4px' },
    }}
  >
    {icon}
    {label}
    {count !== undefined && count > 0 && (
      <Box component="span" sx={{ bgcolor: 'action.selected', borderRadius: '10px', px: 0.9, fontSize: 11.5, fontWeight: 600 }}>
        {count}
      </Box>
    )}
  </Box>
);

const ApiConnectionEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const prefix = useLocation().pathname.startsWith('/admin') ? '/admin' : '';
  const listPath = `${prefix}/automation/api-connections`;
  const { hasFeature } = usePlanFeature();
  const enabled = hasFeature('httpAutomationEnabled');

  const [form, setForm] = useState<Form>(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // For an existing API we never receive secrets back; this drives "unchanged" hints.
  const [has, setHas] = useState({ credential: false, hmac: false, userContext: false, oauthId: false, oauthSecret: false });

  const [clients, setClients] = useState<ExternalApiClient[]>([]);
  const [contactFields, setContactFields] = useState<FieldDefinition[]>([]);
  const [leadFields, setLeadFields] = useState<FieldDefinition[]>([]);
  const [flowFields, setFlowFields] = useState<FieldDefinition[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const [testVars, setTestVars] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  // Simulated end-user identity for the dry run — a live conversation supplies
  // the verified WhatsApp number automatically; the test can't, so act as one.
  const [testSub, setTestSub] = useState('');
  const [testAuthLevel, setTestAuthLevel] = useState<RequiresAuthLevel>('identified');
  const [reqTab, setReqTab] = useState<'request' | 'auth' | 'headers'>('request');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [bodyView, setBodyView] = useState<'tree' | 'raw'>('tree');
  const [copied, setCopied] = useState(false);
  // Every real field in the latest response — the mapping picker offers these, so
  // nobody has to type a path.
  const fields = useMemo(() => (result?.json !== undefined ? flattenJsonLeaves(result.json) : []), [result]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((p) => ({ ...p, [k]: v }));

  // Default the test identity to what the connection requires, so the dry run
  // passes the gate out of the box (the author can still lower it to see a block).
  useEffect(() => {
    if (form.requiresAuthLevel !== 'none') setTestAuthLevel(form.requiresAuthLevel);
  }, [form.requiresAuthLevel]);

  const loadFields = useCallback(async () => {
    setFieldsLoading(true);
    try {
      const [c, l, fl] = await Promise.all([
        adminApi.getFieldDefinitions('contact', { suppressPermissionLock: true }),
        adminApi.getFieldDefinitions('lead', { suppressPermissionLock: true }),
        adminApi.getFieldDefinitions('flow', { suppressPermissionLock: true }),
      ]);
      setContactFields(c);
      setLeadFields(l);
      setFlowFields(fl);
    } catch {
      /* the mapping UI shows an empty-state hint */
    } finally {
      setFieldsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFields();
  }, [loadFields]);

  // One-click "save this name to the registry" from a mapping row. A duplicate
  // (409) just means it's already there — refresh and move on either way.
  const saveFlowVariable = useCallback(
    async (name: string) => {
      try {
        await adminApi.createFlowVariable({ key: name });
      } catch (e: any) {
        if (e?.response?.status !== 409) throw e;
      }
      await loadFields();
    },
    [loadFields]
  );

  // External clients are behind their own permission, so only fetch them once the
  // "External client" type is actually chosen (avoids a permission prompt for
  // everyone who just wants a public API).
  const [clientsLoaded, setClientsLoaded] = useState(false);
  useEffect(() => {
    if (form.kind !== 'external_client' || clientsLoaded) return;
    setClientsLoaded(true);
    adminApi
      .getExternalApiClients()
      .then(setClients)
      .catch(() => {});
  }, [form.kind, clientsLoaded]);

  useEffect(() => {
    if (isNew || !id) return;
    setLoading(true);
    apiConnectionApi
      .get(id)
      .then((c) => {
        const isExt = c.auth.type === 'external_client';
        setForm({
          ...emptyForm,
          name: c.name,
          description: c.description ?? '',
          kind: isExt ? 'external_client' : 'public',
          externalClientId: c.auth.externalClientId ?? '',
          status: c.status,
          method: c.request?.method ?? 'GET',
          endpoint: joinEndpoint(c.baseUrl, c.request?.path, c.request?.queryParams),
          headers: c.request?.headers ?? [],
          bodyMode: c.request?.bodyMode ?? 'none',
          body: c.request?.body ?? '',
          authType: isExt ? 'none' : (c.auth.type as PublicAuth),
          apiKeyHeaderName: c.auth.apiKeyHeaderName ?? '',
          oauthTokenUrl: c.auth.oauth2?.tokenUrl ?? '',
          oauthScopes: (c.auth.oauth2?.scopes ?? []).join(' '),
          requiresAuthLevel: c.requiresAuthLevel,
          sendUserContext: c.sendUserContext,
          timeoutMs: c.timeoutMs != null ? String(c.timeoutMs) : '',
          mappings: c.request?.responseMappings ?? [],
        });
        setHas({
          credential: c.auth.hasCredential,
          hmac: c.auth.hasHmacSecret,
          userContext: c.auth.hasUserContextSecret,
          oauthId: !!c.auth.oauth2?.hasClientId,
          oauthSecret: !!c.auth.oauth2?.hasClientSecret,
        });
      })
      .catch((e: any) => setLoadError(apiErrorMsg(e, 'Could not load this API')))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  // ── {{variables}} found in the request → the Test form ─────────────────────
  const variableNames = useMemo(
    () => extractVariables(form.endpoint, form.body, ...form.headers.map((h) => h.value)),
    [form.endpoint, form.body, form.headers]
  );

  // Grouped choices for the "{}" insert menus — only tokens the engine actually
  // resolves (sync conversation-flow): the customer's identity (system.*), the
  // tenant's contact custom fields (contact.custom.*), the flow-variable registry,
  // and anything this request already references that isn't one of those.
  const insertGroups = useMemo<InsertVarGroup[]>(() => {
    const flowKeys = flowFields.map((f) => f.key);
    const known = new Set<string>([
      'system.customerName',
      'system.customerPhone',
      'system.customerEmail',
      'system.now',
      ...flowKeys,
      ...contactFields.map((f) => `contact.custom.${f.key}`),
    ]);
    const extras = variableNames.filter((n) => n && !known.has(n));
    return [
      {
        label: 'Customer',
        items: [
          { token: 'system.customerName', hint: 'Name' },
          { token: 'system.customerPhone', hint: 'Phone' },
          { token: 'system.customerEmail', hint: 'Email' },
        ],
      },
      {
        label: 'Contact fields',
        items: contactFields.map((f) => ({ token: `contact.custom.${f.key}`, hint: f.label || f.key })),
      },
      { label: 'Flow variables', items: flowKeys.map((k) => ({ token: k })) },
      { label: 'System', items: [{ token: 'system.now', hint: 'Current time (ISO)' }] },
      { label: 'In this request', items: extras.map((t) => ({ token: t })) },
    ];
  }, [flowFields, contactFields, variableNames]);

  // Refs for caret-aware {{variable}} insertion into the request fields.
  const endpointRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const headerRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const insertInto = (
    el: HTMLInputElement | HTMLTextAreaElement | null,
    current: string,
    name: string,
    apply: (next: string) => void
  ) => {
    const { value, caret } = insertAtCaret(el, current, `{{${name}}}`);
    apply(value);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try {
        el.setSelectionRange(caret, caret);
      } catch {
        /* ignore */
      }
    });
  };

  const selectedClient = clients.find((c) => c._id === form.externalClientId);
  const callbackOrigin = useMemo(() => {
    try {
      return selectedClient?.callbackUrl ? new URL(selectedClient.callbackUrl).origin : '';
    } catch {
      return '';
    }
  }, [selectedClient]);
  const endpointOrigin = splitEndpoint(form.endpoint)?.baseUrl ?? '';
  const originMismatch = form.kind === 'external_client' && !!callbackOrigin && !!endpointOrigin && endpointOrigin !== callbackOrigin;

  // ── Test ───────────────────────────────────────────────────────────────────
  const runTest = async () => {
    setTestError(null);
    setResult(null);
    const built = buildInput(form);
    if (!built.input || !built.request) {
      setTestError(built.error ?? 'Fix the form first.');
      return;
    }
    setTesting(true);
    try {
      // Simulate the end-user identity when the connection cares about it — the
      // gate checks authLevel, and sendUserContext builds the signed header from sub.
      const needsIdentity = form.requiresAuthLevel !== 'none' || form.sendUserContext;
      const payload: TestPayload = {
        request: built.request,
        sampleVariables: testVars,
        ...(needsIdentity
          ? { userContext: { sub: testSub.trim() || '+15555550100', channel: 'whatsapp', authLevel: testAuthLevel } }
          : {}),
      };
      const r = isNew
        ? await apiConnectionApi.testDraft(built.input, payload)
        : await apiConnectionApi.testSaved(id as string, payload, built.input.baseUrl);
      setResult(r);
    } catch (e: any) {
      setTestError(apiErrorMsg(e, 'The test could not be run'));
    } finally {
      setTesting(false);
    }
  };

  // ── Mapping ────────────────────────────────────────────────────────────────
  const mappedPaths = useMemo(() => new Set(form.mappings.map((m) => m.source)), [form.mappings]);
  const addMapping = (source = '') => {
    setForm((p) => {
      const taken = new Set(p.mappings.filter((m) => m.target.kind === 'flow').map((m) => (m.target as { name: string }).name));
      const name = source ? suggestVariableName(source, taken) : '';
      return { ...p, mappings: [...p.mappings, { source, target: { kind: 'flow', name } }] };
    });
  };
  const pickPath = (path: string) => {
    if (form.mappings.some((m) => m.source === path)) return; // already mapped
    addMapping(path);
  };
  const updateMapping = (i: number, patch: Partial<ResponseMapping>) =>
    setForm((p) => ({ ...p, mappings: p.mappings.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));
  const setTargetKind = (i: number, kind: VariableTarget['kind']) => {
    const source = form.mappings[i]?.source ?? '';
    const taken = new Set(form.mappings.filter((m) => m.target.kind === 'flow').map((m) => (m.target as { name: string }).name));
    updateMapping(i, {
      target: kind === 'flow' ? { kind: 'flow', name: suggestVariableName(source, taken) } : { kind, key: '' },
    });
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaveError(null);
    const built = buildInput(form);
    if (!built.input) {
      setSaveError(built.error ?? 'Fix the form first.');
      return;
    }
    setSaving(true);
    try {
      if (isNew) await apiConnectionApi.create(built.input);
      else await apiConnectionApi.update(id as string, built.input);
      navigate(listPath);
    } catch (e: any) {
      setSaveError(apiErrorMsg(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const secretHint = (isSet: boolean) => (!isNew && isSet ? '•••••••• saved — leave blank to keep' : '');

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!enabled) {
    return (
      <Box>
        <Typography variant="h4" fontWeight="bold" mb={1}>
          API
        </Typography>
        <Alert severity="info" icon={<Lock />}>
          HTTP API Automation isn’t included in your current plan. Upgrade to connect your flows to external APIs.
        </Alert>
      </Box>
    );
  }
  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)}>
          Back to APIs
        </Button>
      </Box>
    );
  }

  const fieldOptions = (kind: 'contactField' | 'leadField') => (kind === 'contactField' ? contactFields : leadFields);
  const setHeader = (i: number, patch: Partial<KeyValue>) => set('headers', form.headers.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const queryChips = splitEndpoint(form.endpoint)?.queryParams ?? [];
  const previewUrl = renderPreviewUrl(form.endpoint, testVars);
  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(result?.json !== undefined ? JSON.stringify(result.json, null, 2) : result?.body ?? '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard can be blocked — nothing to do */
    }
  };

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)} sx={{ textTransform: 'none', color: 'text.secondary', mb: 1.5, ml: -1 }}>
        All APIs
      </Button>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ ...CARD, width: 52, height: 52, display: 'grid', placeItems: 'center', bgcolor: 'action.hover' }}>
            <Webhook sx={{ color: 'text.secondary' }} />
          </Box>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography fontSize={26} fontWeight={700} lineHeight={1.2}>
                {isNew ? 'New API' : form.name || 'Edit API'}
              </Typography>
              <Chip size="small" label={form.status === 'active' ? 'Active' : 'Disabled'} sx={{ fontWeight: 600, bgcolor: form.status === 'active' ? 'rgba(29,107,74,0.12)' : 'action.selected', color: form.status === 'active' ? ACCENT : 'text.secondary' }} />
            </Stack>
            <Typography color="text.secondary" fontSize={14.5}>
              Request configuration &amp; response mapping
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <Button onClick={() => navigate(listPath)} sx={{ textTransform: 'none', color: 'text.primary', px: 2.5 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={save}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Check />}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
          >
            {saving ? 'Saving…' : isNew ? 'Create API' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1.15fr) minmax(0,0.85fr)' }, alignItems: 'start' }}>
        {/* ═══════════════ LEFT ═══════════════ */}
        <Box>
          {/* 01 Details */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, pb: 4 }}>
            <SectionHeader n="01" title="Details" />
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <Box sx={{ flex: 1 }}>
                  <Label>API name</Label>
                  <TextField size="small" fullWidth required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Get gender" sx={FIELD_SX} />
                </Box>
                <FormControlLabel
                  sx={{ mb: 0.5, mr: 0 }}
                  control={<Switch checked={form.status === 'active'} onChange={(e) => set('status', e.target.checked ? 'active' : 'disabled')} />}
                  label={<Typography fontSize={13}>{form.status === 'active' ? 'Active' : 'Disabled'}</Typography>}
                />
              </Stack>
              <Box>
                <Label optional>Description</Label>
                <TextField size="small" fullWidth value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What this API does and when to use it" sx={FIELD_SX} />
              </Box>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={form.kind}
                onChange={(_, v: Kind | null) => v && set('kind', v)}
                sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: '10px', '& .MuiToggleButton-root': { border: 0, borderRadius: '8px !important', textTransform: 'none', fontWeight: 500, py: 1, color: 'text.secondary', gap: 1 }, '& .Mui-selected': { bgcolor: 'background.paper !important', color: 'text.primary', boxShadow: 1, fontWeight: 600 } }}
              >
                <ToggleButton value="public">
                  <Webhook fontSize="small" /> Public / third-party
                </ToggleButton>
                <ToggleButton value="external_client">
                  <Code fontSize="small" /> External client
                </ToggleButton>
              </ToggleButtonGroup>
              {form.kind === 'external_client' && (
                <Stack spacing={1.5}>
                  <TextField
                    select
                    label="External client"
                    size="small"
                    fullWidth
                    required
                    value={form.externalClientId}
                    onChange={(e) => set('externalClientId', e.target.value)}
                    sx={FIELD_SX}
                    helperText={
                      clients.length === 0
                        ? 'No external clients yet — create one under Settings › API Integration.'
                        : selectedClient?.callbackUrl
                          ? `Requests are signed with this client’s secret and can only go to ${callbackOrigin}.`
                          : selectedClient
                            ? 'This client has no callback URL yet — set one in Settings › API Integration.'
                            : 'Requests are signed with this client’s secret.'
                    }
                  >
                    {clients
                      .filter((c) => c.status === 'active')
                      .map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          {c.name}
                          {c.label ? ` — ${c.label}` : ''}
                        </MenuItem>
                      ))}
                  </TextField>
                  {callbackOrigin && !form.endpoint && (
                    <Button size="small" onClick={() => set('endpoint', `${callbackOrigin}/`)} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                      Use the client’s host
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>

          {/* 02 Request */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, pb: 4 }}>
            <SectionHeader n="02" title="Build your request" />
            <Box sx={{ ...CARD, display: 'flex', alignItems: 'stretch', overflow: 'hidden', borderColor: originMismatch ? 'error.main' : 'divider' }}>
              <Select
                value={form.method}
                onChange={(e) => set('method', e.target.value as HttpMethod)}
                variant="standard"
                disableUnderline
                sx={{ minWidth: 104, px: 1.5, bgcolor: 'action.hover', fontWeight: 700, fontSize: 13.5, borderRight: 1, borderColor: 'divider', '& .MuiSelect-select': { py: 1.6, display: 'flex', alignItems: 'center' } }}
              >
                {METHODS.map((m) => (
                  <MenuItem key={m} value={m} sx={{ fontWeight: 600, fontSize: 13.5 }}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
              <InputBase
                fullWidth
                inputRef={endpointRef}
                value={form.endpoint}
                onChange={(e) => set('endpoint', e.target.value)}
                placeholder="https://api.example.com/v1/users?name={{name}}"
                inputProps={{ 'aria-label': 'Endpoint URL', style: MONO }}
                sx={{ px: 2, py: 0.5 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', pr: 0.5, borderLeft: 1, borderColor: 'divider' }}>
                <InsertVarMenu
                  groups={insertGroups}
                  title="Insert a variable into the URL"
                  onPick={(name) => insertInto(endpointRef.current, form.endpoint, name, (v) => set('endpoint', v))}
                />
              </Box>
            </Box>
            <Typography variant="caption" color={originMismatch ? 'error' : 'text.secondary'} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
              {originMismatch ? (
                `Must be on ${callbackOrigin} (the client’s callback host).`
              ) : (
                <>
                  <DataObject sx={{ fontSize: 15 }} /> Dynamic values use <code>{'{{variable}}'}</code>
                </>
              )}
            </Typography>

            <Box sx={{ mt: 2.5, borderBottom: 1, borderColor: 'divider' }}>
              <Tab active={reqTab === 'request'} icon={<Tune sx={{ fontSize: 17 }} />} label="Request" onClick={() => setReqTab('request')} />
              {form.kind === 'public' && <Tab active={reqTab === 'auth'} icon={<VpnKey sx={{ fontSize: 17 }} />} label="Authentication" onClick={() => setReqTab('auth')} />}
              <Tab active={reqTab === 'headers'} icon={<Code sx={{ fontSize: 17 }} />} label="Headers" count={form.headers.filter((h) => h.key.trim()).length} onClick={() => setReqTab('headers')} />
            </Box>

            <Box sx={{ pt: 2.5 }}>
              {reqTab === 'request' && (
                <Stack spacing={2.5}>
                  <Box>
                    <Label>Query parameters</Label>
                    {queryChips.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        None — add <code>?key=value</code> to the URL above.
                      </Typography>
                    ) : (
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {queryChips.map((q, i) => (
                          <Box key={i} sx={{ ...MONO, ...CARD, borderRadius: '8px', px: 1.25, py: 0.5, bgcolor: 'action.hover' }}>
                            {q.key} <span style={{ opacity: 0.5 }}>=</span> {q.value}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  {form.method !== 'GET' && (
                    <Box>
                      <Label>Body</Label>
                      <ToggleButtonGroup exclusive size="small" value={form.bodyMode} onChange={(_, v: BodyMode | null) => v && set('bodyMode', v)} sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}>
                        <ToggleButton value="none">None</ToggleButton>
                        <ToggleButton value="json">JSON</ToggleButton>
                        <ToggleButton value="form">Form</ToggleButton>
                        <ToggleButton value="raw">Raw</ToggleButton>
                      </ToggleButtonGroup>
                      {form.bodyMode !== 'none' && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <InsertVarMenu
                              groups={insertGroups}
                              title="Insert a variable into the body"
                              onPick={(name) => insertInto(bodyRef.current, form.body, name, (v) => set('body', v))}
                            />
                          </Box>
                          <TextField
                            fullWidth
                            multiline
                            minRows={6}
                            size="small"
                            inputRef={bodyRef}
                            sx={{ ...FIELD_SX }}
                            value={form.body}
                            onChange={(e) => set('body', e.target.value)}
                            placeholder={form.bodyMode === 'raw' ? 'Plain text body' : '{\n  "name": "{{input.name}}",\n  "email": "{{input.email}}"\n}'}
                            helperText={form.bodyMode === 'form' ? 'Write the fields as a JSON object — it is sent as a form.' : form.bodyMode === 'json' ? 'Values are inserted safely — a customer can’t break out of the JSON.' : undefined}
                            inputProps={{ style: MONO }}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </Stack>
              )}

              {reqTab === 'headers' && (
                <Stack spacing={1.25}>
                  {form.headers.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No custom headers. Authentication headers are added for you.
                    </Typography>
                  )}
                  {form.headers.map((h, i) => (
                    <Stack direction="row" spacing={1.25} key={i}>
                      <TextField size="small" placeholder="Header name" value={h.key} onChange={(e) => setHeader(i, { key: e.target.value })} sx={{ width: '40%', ...FIELD_SX }} inputProps={{ style: MONO }} />
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Value or {{variable}}"
                        value={h.value}
                        onChange={(e) => setHeader(i, { value: e.target.value })}
                        inputRef={(el: HTMLInputElement | null) => {
                          headerRefs.current[i] = el;
                        }}
                        sx={FIELD_SX}
                        inputProps={{ style: MONO }}
                        InputProps={{
                          endAdornment: (
                            <InsertVarMenu
                              groups={insertGroups}
                              title="Insert a variable into this header"
                              onPick={(name) => insertInto(headerRefs.current[i] ?? null, h.value, name, (v) => setHeader(i, { value: v }))}
                            />
                          ),
                        }}
                      />
                      <IconButton onClick={() => set('headers', form.headers.filter((_, j) => j !== i))} aria-label="Remove header">
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button startIcon={<Add />} onClick={() => set('headers', [...form.headers, { key: '', value: '' }])} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}>
                    Add header
                  </Button>
                </Stack>
              )}

              {reqTab === 'auth' && form.kind === 'public' && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Stored encrypted; never shown again after saving.
                  </Typography>
                  <TextField select label="Auth type" size="small" fullWidth value={form.authType} onChange={(e) => set('authType', e.target.value as PublicAuth)} sx={FIELD_SX}>
                    {(Object.keys(AUTH_LABELS) as PublicAuth[]).map((t) => (
                      <MenuItem key={t} value={t}>
                        {AUTH_LABELS[t]}
                      </MenuItem>
                    ))}
                  </TextField>
                  {(form.authType === 'bearer' || form.authType === 'api_key_header' || form.authType === 'basic') && (
                    <TextField label={form.authType === 'bearer' ? 'Token' : form.authType === 'basic' ? 'user:password' : 'API key'} size="small" fullWidth type="password" value={form.credential} placeholder={secretHint(has.credential)} onChange={(e) => set('credential', e.target.value)} sx={FIELD_SX} />
                  )}
                  {form.authType === 'api_key_header' && (
                    <TextField label="Header name" size="small" fullWidth placeholder="X-Api-Key" value={form.apiKeyHeaderName} onChange={(e) => set('apiKeyHeaderName', e.target.value)} sx={FIELD_SX} />
                  )}
                  {form.authType === 'hmac' && (
                    <TextField label="Signing secret" size="small" fullWidth type="password" value={form.hmacSecret} placeholder={secretHint(has.hmac)} onChange={(e) => set('hmacSecret', e.target.value)} helperText="Each request body is signed so your API can verify it came from us." sx={FIELD_SX} />
                  )}
                  {form.authType === 'oauth2_client_credentials' && (
                    <>
                      <TextField label="Token URL" size="small" fullWidth placeholder="https://auth.example.com/oauth/token" value={form.oauthTokenUrl} onChange={(e) => set('oauthTokenUrl', e.target.value)} sx={FIELD_SX} />
                      <Stack direction="row" spacing={1.25}>
                        <TextField label="Client ID" size="small" fullWidth type="password" value={form.oauthClientId} placeholder={secretHint(has.oauthId)} onChange={(e) => set('oauthClientId', e.target.value)} sx={FIELD_SX} />
                        <TextField label="Client secret" size="small" fullWidth type="password" value={form.oauthClientSecret} placeholder={secretHint(has.oauthSecret)} onChange={(e) => set('oauthClientSecret', e.target.value)} sx={FIELD_SX} />
                      </Stack>
                      <TextField label="Scopes (space-separated)" size="small" fullWidth value={form.oauthScopes} onChange={(e) => set('oauthScopes', e.target.value)} sx={FIELD_SX} />
                    </>
                  )}
                </Stack>
              )}
            </Box>

            {/* Advanced */}
            <Box sx={{ ...CARD, mt: 3, overflow: 'hidden' }}>
              <Box
                component="button"
                type="button"
                onClick={() => setAdvancedOpen((o) => !o)}
                aria-expanded={advancedOpen}
                sx={{ all: 'unset', boxSizing: 'border-box', width: '100%', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Tune sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography fontSize={14} fontWeight={600} sx={{ flex: 1 }}>
                  Advanced settings
                </Typography>
                <ExpandMore sx={{ transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: '150ms', color: 'text.secondary' }} />
              </Box>
              {advancedOpen && (
                <Stack spacing={2} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <TextField
                    select
                    label="User type"
                    size="small"
                    fullWidth
                    value={form.requiresAuthLevel}
                    onChange={(e) => set('requiresAuthLevel', e.target.value as RequiresAuthLevel)}
                    helperText={
                      form.requiresAuthLevel === 'none'
                        ? 'Any user — store-wide data, no customer identity needed (catalog, stock, hours).'
                        : form.requiresAuthLevel === 'identified'
                          ? 'A specific customer’s own data, where their identified WhatsApp number is proof enough (my orders, my bag). This is automatic on every conversation.'
                          : 'Sensitive or risky — requires a “Mark Verified” step (PIN/OTP) earlier in the flow before this can run (payments, account changes).'
                    }
                    sx={FIELD_SX}
                  >
                    {(Object.keys(AUTH_LEVEL_LABELS) as RequiresAuthLevel[]).map((l) => (
                      <MenuItem key={l} value={l}>
                        {AUTH_LEVEL_LABELS[l]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel control={<Switch checked={form.sendUserContext} onChange={(e) => set('sendUserContext', e.target.checked)} />} label={<Typography fontSize={13.5}>Send a signed customer-identity header (X-Baatly-User-Context)</Typography>} />
                  {form.sendUserContext && form.kind === 'public' && (
                    <TextField label="Identity-header signing secret" size="small" fullWidth type="password" value={form.userContextSecret} placeholder={secretHint(has.userContext)} onChange={(e) => set('userContextSecret', e.target.value)} helperText="A dedicated secret (not your API credential) so your server can verify who the customer is." sx={FIELD_SX} />
                  )}
                  {form.sendUserContext && form.kind === 'external_client' && (
                    <Typography variant="caption" color="text.secondary">
                      The identity header is signed with a key derived from the client’s secret — nothing extra to enter.
                    </Typography>
                  )}
                  <TextField
                    label="Timeout (seconds)"
                    size="small"
                    type="number"
                    value={form.timeoutMs ? String(Number(form.timeoutMs) / 1000) : ''}
                    placeholder="8"
                    onChange={(e) => set('timeoutMs', e.target.value ? String(Math.round(Number(e.target.value) * 1000)) : '')}
                    sx={{ maxWidth: 200, ...FIELD_SX }}
                  />
                </Stack>
              )}
            </Box>
          </Box>

          {/* 03 Map the response */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, pb: 4 }}>
            <SectionHeader
              n="03"
              title="Map the response"
              right={
                <Typography variant="body2" color="text.secondary">
                  {form.mappings.length} {form.mappings.length === 1 ? 'variable' : 'variables'}
                </Typography>
              }
            />
            {fields.length === 0 && (
              <Alert severity="info" icon={<DataObject fontSize="small" />} sx={{ mb: 2, borderRadius: '10px' }}>
                {form.mappings.length > 0
                  ? 'Send a test request (right) to see the response — then you can check or change which field each variable reads.'
                  : 'Send a test request (right), then pick the fields you want to use in your flow.'}
              </Alert>
            )}
            <Stack spacing={2}>
              {form.mappings.map((m, i) => {
                const kind = m.target.kind;
                const flowName = kind === 'flow' ? (m.target as { name: string }).name : '';
                const flowNameValid = isValidVariableName(flowName);
                // When the name is bad (e.g. the response path got pasted in), offer a
                // clean one from the source — "items[0].productId" → "productId".
                const suggestedName =
                  kind === 'flow' && !flowNameValid
                    ? suggestVariableName(
                        m.source || flowName,
                        new Set(
                          form.mappings
                            .filter((x, j) => j !== i && x.target.kind === 'flow')
                            .map((x) => (x.target as { name: string }).name)
                        )
                      )
                    : '';
                const defs = kind === 'flow' ? [] : fieldOptions(kind);
                const selectedDef = kind === 'flow' ? null : defs.find((d) => d.key === (m.target as { key: string }).key) ?? null;
                const current: ResponseField | null = m.source
                  ? fields.find((f) => f.path === m.source) ?? { path: m.source, value: undefined, type: 'string' }
                  : null;
                const options = current && !fields.some((f) => f.path === current.path) ? [current, ...fields] : fields;
                const missing = result?.json !== undefined && !!m.source && resolveJsonPath(result.json, m.source) === undefined;
                return (
                  <Box key={i} sx={{ ...CARD, p: 1.5, borderColor: missing ? 'warning.main' : 'divider' }}>
                    <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(0,0.8fr) 24px minmax(0,1.25fr) 36px' }, alignItems: 'start' }}>
                      <Box>
                        <Typography sx={{ fontSize: 11, letterSpacing: 0.6, color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>RESPONSE FIELD</Typography>
                        <Autocomplete
                          size="small"
                          options={options}
                          value={current}
                          disabled={fields.length === 0}
                          getOptionLabel={(o) => o.path}
                          isOptionEqualToValue={(a, b) => a.path === b.path}
                          onChange={(_, f) => {
                            const taken = new Set(form.mappings.filter((x, j) => j !== i && x.target.kind === 'flow').map((x) => (x.target as { name: string }).name));
                            updateMapping(i, {
                              source: f?.path ?? '',
                              ...(f && m.target.kind === 'flow' && !m.target.name ? { target: { kind: 'flow', name: suggestVariableName(f.path, taken) } as VariableTarget } : {}),
                            });
                          }}
                          renderOption={(props, o) => (
                            <li {...props} key={o.path} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                              <span style={MONO}>{o.path}</span>
                              <span style={{ opacity: 0.6, fontSize: 12 }}>{previewValue(o.value, 24)}</span>
                            </li>
                          )}
                          renderInput={(p) => <TextField {...(p as any)} placeholder={fields.length ? 'Choose a field…' : 'Send a test request first'} sx={FIELD_SX} inputProps={{ ...(p as any).inputProps, style: MONO }} />}
                        />
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'grid' }, placeItems: 'center', height: 40, mt: 2.75, color: 'text.secondary' }}>
                        <ArrowForward fontSize="small" />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 11, letterSpacing: 0.6, color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>SAVE AS</Typography>
                        <Stack direction="row" spacing={1}>
                          <TextField select size="small" value={kind} onChange={(e) => setTargetKind(i, e.target.value as VariableTarget['kind'])} sx={{ width: 104, flexShrink: 0, ...FIELD_SX }}>
                            <MenuItem value="flow">Variable</MenuItem>
                            <MenuItem value="contactField">Contact field</MenuItem>
                            <MenuItem value="leadField">Lead field</MenuItem>
                          </TextField>
                          {kind === 'flow' ? (
                            <FlowVariablePicker
                              value={(m.target as { name: string }).name}
                              onChange={(name) => updateMapping(i, { target: { kind: 'flow', name } })}
                              options={flowFields.map((f) => ({ key: f.key, label: f.label, description: f.description, type: f.type }))}
                              loading={fieldsLoading}
                              onRefresh={() => void loadFields()}
                              error={!isValidVariableName((m.target as { name: string }).name)}
                              fieldSx={FIELD_SX}
                              mono={MONO}
                              compact
                              onCreate={saveFlowVariable}
                            />
                          ) : (
                            <Autocomplete
                              size="small"
                              fullWidth
                              options={defs}
                              value={selectedDef}
                              getOptionLabel={(d) => `${d.label} (${d.key})`}
                              isOptionEqualToValue={(a, b) => a.key === b.key}
                              onChange={(_, d) => updateMapping(i, { target: { kind, key: d?.key ?? '' } as VariableTarget })}
                              noOptionsText={`No ${kind === 'contactField' ? 'contact' : 'lead'} fields defined yet`}
                              loading={fieldsLoading}
                              renderInput={(p) => <TextField {...(p as any)} placeholder="Choose a field…" error={!!(m.target as { key: string }).key && !selectedDef} sx={FIELD_SX} />}
                            />
                          )}
                        </Stack>
                        {kind === 'flow' && !flowNameValid ? (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
                            A name can’t be a path — letters, numbers and _ only.{' '}
                            {suggestedName && (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => updateMapping(i, { target: { kind: 'flow', name: suggestedName } })}
                                sx={{ ...MONO, fontSize: 11.5 }}
                              >
                                Use “{suggestedName}”
                              </Link>
                            )}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right', ...(kind === 'flow' ? MONO : {}) }}>
                            {kind === 'flow'
                              ? `{{${flowName}}}`
                              : kind === 'contactField'
                                ? 'Saved on the contact — works in customer-message flows'
                                : 'Saved on the lead — works in lead / campaign flows'}
                          </Typography>
                        )}
                      </Box>
                      <IconButton onClick={() => set('mappings', form.mappings.filter((_, j) => j !== i))} aria-label="Remove mapping" sx={{ mt: 2.75 }}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Box>
                    {missing ? (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                        “{m.source}” isn’t in the latest response — pick the field again.
                      </Typography>
                    ) : current && current.value !== undefined ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Sample value: <span style={MONO}>{previewValue(current.value, 80)}</span>
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mt={2}>
              <Tooltip title={fields.length ? '' : 'Send a test request first so you can pick from the real response'}>
                <span>
                  <Button startIcon={<Add />} onClick={() => addMapping()} disabled={fields.length === 0} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Add mapping
                  </Button>
                </span>
              </Tooltip>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Link href={`${prefix}/settings?tab=flow-vars`} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  New variable <OpenInNew sx={{ fontSize: 13 }} />
                </Link>
                <Link href={`${prefix}/settings?tab=fields`} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  Custom field <OpenInNew sx={{ fontSize: 13 }} />
                </Link>
                <Tooltip title="Reload variables & fields">
                  <span>
                    <IconButton size="small" onClick={loadFields} disabled={fieldsLoading}>
                      {fieldsLoading ? <CircularProgress size={14} /> : <Refresh fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* ═══════════════ RIGHT: test + response ═══════════════ */}
        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
          <Box sx={{ ...CARD, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'action.hover' }}>
                <Terminal sx={{ color: 'text.secondary' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700}>Test &amp; response</Typography>
                <Typography variant="caption" color="text.secondary">
                  Sends a real request with the sample values below
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ p: 2.5 }}>
              <Typography fontSize={13.5} fontWeight={600} mb={1}>
                Sample variables
              </Typography>
              {variableNames.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This request has no <code>{'{{variables}}'}</code>.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {variableNames.map((v) => (
                    <Box key={v} sx={{ ...CARD, borderRadius: '8px', display: 'flex', alignItems: 'stretch', overflow: 'hidden' }}>
                      <Box sx={{ ...MONO, px: 1.5, display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRight: 1, borderColor: 'divider', whiteSpace: 'nowrap' }}>{`{{${v}}}`}</Box>
                      <InputBase fullWidth placeholder="Sample value" value={testVars[v] ?? ''} onChange={(e) => setTestVars((p) => ({ ...p, [v]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && !testing && runTest()} inputProps={{ 'aria-label': `Sample value for ${v}` }} sx={{ px: 1.5, py: 0.75 }} />
                    </Box>
                  ))}
                </Stack>
              )}

              {(form.requiresAuthLevel !== 'none' || form.sendUserContext) && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography fontSize={13.5} fontWeight={600} mb={0.5}>
                    Test as a customer
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    A live flow passes the customer’s verified WhatsApp number automatically. The test can’t, so act as one:
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Customer number"
                      placeholder="+15555550100"
                      value={testSub}
                      onChange={(e) => setTestSub(e.target.value)}
                      sx={FIELD_SX}
                      inputProps={{ style: MONO }}
                    />
                    <TextField
                      select
                      size="small"
                      label="User type"
                      value={testAuthLevel}
                      onChange={(e) => setTestAuthLevel(e.target.value as RequiresAuthLevel)}
                      sx={{ minWidth: 148, ...FIELD_SX }}
                    >
                      <MenuItem value="none">Any</MenuItem>
                      <MenuItem value="identified">Specific</MenuItem>
                      <MenuItem value="verified">Verified</MenuItem>
                    </TextField>
                  </Stack>
                  {form.requiresAuthLevel !== 'none' && (
                    <Typography
                      variant="caption"
                      color={AUTH_RANK[testAuthLevel] >= AUTH_RANK[form.requiresAuthLevel] ? 'text.secondary' : 'warning.main'}
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {AUTH_RANK[testAuthLevel] >= AUTH_RANK[form.requiresAuthLevel]
                        ? `Meets this connection’s required user type (${AUTH_LEVEL_SHORT[form.requiresAuthLevel]}).`
                        : `This connection requires ‘${AUTH_LEVEL_SHORT[form.requiresAuthLevel]}’ — raise the user type above or the call is blocked.`}
                    </Typography>
                  )}
                </Box>
              )}

              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 2.5, mb: 2 }}>
                <Box sx={{ ...MONO, fontSize: 11.5, fontWeight: 700, px: 1, py: 0.4, borderRadius: '6px', bgcolor: 'action.selected' }}>{form.method}</Box>
                <Typography sx={{ ...MONO, fontSize: 12, wordBreak: 'break-all', color: 'text.secondary' }}>{previewUrl || 'Enter an endpoint URL'}</Typography>
              </Stack>

              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={runTest}
                disabled={testing}
                startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <FlashOn />}
                sx={{ textTransform: 'none', fontWeight: 600, py: 1.3, borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}
              >
                {testing ? 'Sending…' : 'Send test request'}
              </Button>
              {!isNew && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Uses the saved credentials — save first to test changed ones.
                </Typography>
              )}
              {testError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {testError}
                </Alert>
              )}

              {result && (
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 2.5 }}>
                  <Chip size="small" label={result.status ? `${result.status} ${result.ok ? 'OK' : ''}`.trim() : 'No response'} sx={{ fontWeight: 600, bgcolor: result.ok ? 'rgba(29,107,74,0.12)' : 'rgba(211,47,47,0.12)', color: result.ok ? ACCENT : 'error.main' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    Live response
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {result.durationMs} ms
                  </Typography>
                </Stack>
              )}
              {result && (result.blocked || (!result.ok && result.error)) && (
                <Alert severity={result.blocked ? 'warning' : 'error'} sx={{ mt: 1.5 }}>
                  {result.blocked ? `Blocked: ${result.error}` : result.error}
                </Alert>
              )}

              <Box sx={{ ...CARD, borderRadius: '10px', mt: 2, overflow: 'hidden', bgcolor: 'background.default' }}>
                <Stack direction="row" alignItems="center" sx={{ px: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ flex: 1 }}>
                    <Tab active icon={null} label="Body" onClick={() => {}} />
                  </Box>
                  {result?.json !== undefined && (
                    <ToggleButtonGroup exclusive size="small" value={bodyView} onChange={(_, v) => v && setBodyView(v)} sx={{ mr: 1, '& .MuiToggleButton-root': { textTransform: 'none', py: 0.1, px: 1, fontSize: 12, border: 0 } }}>
                      <ToggleButton value="tree">Tree</ToggleButton>
                      <ToggleButton value="raw">Raw</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  <Tooltip title={copied ? 'Copied' : 'Copy response'}>
                    <span>
                      <IconButton size="small" onClick={copyBody} disabled={!result?.body && result?.json === undefined}>
                        {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Box sx={{ p: 1.5, minHeight: 190, maxHeight: 420, overflow: 'auto' }}>
                  {!result ? (
                    <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ minHeight: 160, color: 'text.secondary' }}>
                      <DataObject sx={{ fontSize: 34, opacity: 0.6 }} />
                      <Typography fontSize={13.5}>Your response will appear here</Typography>
                      <Typography variant="caption">Then click any value to map it</Typography>
                    </Stack>
                  ) : result.json !== undefined && bodyView === 'tree' ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Click a value to save it as a variable.
                      </Typography>
                      <JsonNode value={result.json} path="" depth={0} mapped={mappedPaths} onPick={pickPath} />
                    </>
                  ) : (
                    <Box component="pre" sx={{ ...MONO, m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {result.json !== undefined ? JSON.stringify(result.json, null, 2) : (result.body || '(empty response)').slice(0, 8000)}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2.5, px: 0.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'action.hover' }}>
              <Webhook sx={{ color: 'text.secondary' }} />
            </Box>
            <Box>
              <Typography fontWeight={600} fontSize={14}>
                Use it in a workflow
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pick this API in a “Call API” step.{' '}
                <Link href={`${prefix}/automation/workflows`} underline="hover">
                  Go to workflows
                </Link>
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* Save failures surface here as a bottom-right toast — the save button
          navigates away on success, so an inline banner would be missed. */}
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={8000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setSaveError(null)} sx={{ maxWidth: 480 }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiConnectionEditor;
